import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { downloadCsv, csvFilename } from "@/lib/csv";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, ComposedChart, Line,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import {
  Activity, TrendingUp, Users, Radio, MousePointerClick, AlertTriangle,
  CalendarDays, Clock3, Briefcase, PartyPopper, Download, UserPlus,
} from "lucide-react";

const FOREST = "#1A4331";
const TERRACOTTA = "#D36135";
const AMBER = "#d97706";

const fmtDay = (d) => {
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  } catch { return d; }
};

const AXIS_TICK = { fontSize: 11, fill: "#5c5c5c" };

function StatCard({ label, value, sub, testId, live }) {
  return (
    <div data-testid={testId} className="bg-white border border-neutral-200 rounded-xl p-5">
      <div className="text-[10px] uppercase tracking-[0.16em] text-neutral-500 font-bold flex items-center gap-1.5">
        {live && <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse inline-block" />}
        {label}
      </div>
      <div className="font-display text-3xl text-forest mt-1.5 leading-none">{value}</div>
      {sub && <div className="text-xs text-neutral-500 mt-1.5">{sub}</div>}
    </div>
  );
}

function ExportButton({ testId, onClick }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      title="Download the underlying data as CSV"
      className="ml-auto shrink-0 inline-flex items-center gap-1 text-[11px] text-neutral-500 border border-neutral-200 rounded-full px-2.5 py-1 hover:border-forest hover:text-forest transition-colors"
    >
      <Download className="w-3 h-3" /> Export CSV
    </button>
  );
}

function ChartCard({ title, icon: Icon, note, children, testId, onExport, exportTestId }) {
  return (
    <div data-testid={testId} className="bg-white border border-neutral-200 rounded-xl p-5">
      <div className="flex items-start gap-2">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.16em] text-forest/70 font-bold flex items-center gap-1.5">
            {Icon && <Icon className="w-3.5 h-3.5" />} {title}
          </div>
          {note && <div className="text-[11px] text-neutral-400 mt-0.5">{note}</div>}
        </div>
        {onExport && <ExportButton testId={exportTestId} onClick={onExport} />}
      </div>
      <div className="h-56 mt-4">{children}</div>
    </div>
  );
}

function RangePicker({ value, onChange }) {
  return (
    <div className="inline-flex rounded-full border border-neutral-200 bg-white p-1" data-testid="analytics-range-picker">
      {[7, 30, 90].map((d) => (
        <button
          key={d}
          data-testid={`analytics-range-${d}`}
          onClick={() => onChange(d)}
          className={`text-xs px-4 py-1.5 rounded-full transition-colors ${value === d ? "bg-forest text-white" : "text-neutral-600 hover:text-forest"}`}
        >
          {d}d
        </button>
      ))}
    </div>
  );
}

function RateChip({ label, count, pct, tone }) {
  const tones = {
    green: "bg-forest/10 text-forest",
    red: "bg-red-100 text-red-700",
    amber: "bg-amber-100 text-amber-800",
  };
  return (
    <div className={`rounded-full px-3.5 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 ${tones[tone]}`}>
      {label} <span className="font-bold">{pct}%</span>
      <span className="opacity-70">({count})</span>
    </div>
  );
}

function QualityFlags() {
  const [data, setData] = useState(null);
  useEffect(() => { api.adminAnalyticsQualityFlags().then(setData).catch(() => setData({ rules: [] })); }, []);
  if (!data) return null;
  const exportFlags = () => {
    const rows = data.rules.flatMap((rule) => rule.items.map((it) => ({
      rule_id: rule.id, rule_label: rule.label, org_user_id: it.org_user_id,
      org_name: it.org_name, verification_status: it.verification_status || "",
      total_applications: it.total_applications, rejected: it.rejected, link: it.link,
    })));
    downloadCsv(csvFilename("quality-flags"), rows,
      ["rule_id", "rule_label", "org_user_id", "org_name", "verification_status", "total_applications", "rejected", "link"]);
  };
  return (
    <div className="mt-8" data-testid="quality-flags-section">
      <div className="flex items-center gap-3">
        <h3 className="font-display text-xl text-forest tracking-tight flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" /> Quality flags
        </h3>
        <ExportButton testId="export-csv-quality-flags" onClick={exportFlags} />
      </div>
      <p className="text-neutral-600 text-sm mt-1">Automatic review prompts. New rules can be added server-side — the list below renders whatever the rules engine returns.</p>
      <div className="mt-4 space-y-3">
        {data.rules.map((rule) => (
          <div key={rule.id} data-testid={`quality-rule-${rule.id}`} className="bg-white border border-neutral-200 rounded-xl p-5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-forest">{rule.label}</span>
              <Badge className={`rounded-full border-none text-[10px] ${rule.count > 0 ? "bg-red-100 text-red-700" : "bg-forest/10 text-forest"}`}>
                {rule.count > 0 ? `${rule.count} flagged` : "All clear"}
              </Badge>
            </div>
            <p className="text-xs text-neutral-500 mt-1">{rule.description}</p>
            {rule.items.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {rule.items.map((it) => (
                  <div key={it.org_user_id} data-testid={`quality-flag-item-${it.org_user_id}`} className="flex items-center gap-3 text-sm border border-red-100 bg-red-50/50 rounded-lg px-3 py-2 flex-wrap">
                    <Link to={it.link} className="font-medium text-forest hover:underline">{it.org_name}</Link>
                    {it.verification_status && (
                      <span className="text-[10px] uppercase tracking-wide text-neutral-400">{it.verification_status}</span>
                    )}
                    <span className="text-xs text-neutral-600 ml-auto">
                      {it.rejected}/{it.total_applications} applications rejected · 100%
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Phase 17-C item 9: signups by first-touch source/campaign/week + referral
// counts — the GTM prerequisite ("which channel is working?").
function AcquisitionSection({ days }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    api.adminAnalyticsAcquisition(days)
      .then((d) => alive && setData(d))
      .catch(() => alive && setFailed(true));
    return () => { alive = false; };
  }, [days]);

  if (failed) return null;
  const weekly = data?.weekly || [];
  const sources = data?.sources || [];
  const refs = data?.referrals;
  const maxSource = sources[0]?.count || 1;

  return (
    <div data-testid="acquisition-section">
      <div className="flex items-center gap-2 mt-8">
        <h3 className="font-display text-xl text-forest tracking-tight flex items-center gap-2">
          <UserPlus className="w-5 h-5" /> Acquisition
        </h3>
        <span className="text-[11px] text-neutral-400">first-touch UTM attribution, captured at signup</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div data-testid="acquisition-sources" className="bg-white border border-neutral-200 rounded-xl p-5">
          <div className="text-xs uppercase tracking-[0.16em] text-forest/70 font-bold">Signups by source ({days}d)</div>
          <div className="mt-4 space-y-2">
            {sources.length === 0 && <div className="text-sm text-neutral-500">No signups in this range.</div>}
            {sources.map((s) => (
              <div key={s.source} data-testid={`acq-source-${s.source}`}>
                <div className="flex items-center justify-between text-xs">
                  <span className="font-mono text-forest truncate">{s.source}</span>
                  <span className="text-neutral-500 shrink-0 ml-2">{s.count}</span>
                </div>
                <div className="h-1.5 bg-neutral-100 rounded-full mt-1">
                  <div className="h-1.5 bg-terracotta/60 rounded-full" style={{ width: `${Math.max(4, (s.count / maxSource) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
          {refs && (
            <div className="mt-5 pt-4 border-t border-neutral-100" data-testid="acquisition-referrals">
              <div className="text-xs uppercase tracking-[0.16em] text-forest/70 font-bold">Referral codes</div>
              <div className="text-sm text-neutral-600 mt-2">{refs.total} signup{refs.total !== 1 ? "s" : ""} via referral links</div>
              {(refs.by_referrer || []).slice(0, 5).map((r, i) => (
                <div key={i} className="flex items-center justify-between text-xs mt-1.5">
                  <span className="text-forest truncate">{r.referrer_name || "(deleted user)"}{r.code ? ` · ${r.code}` : ""}</span>
                  <span className="text-neutral-500 shrink-0 ml-2">{r.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-2 bg-white border border-neutral-200 rounded-xl p-5" data-testid="acquisition-weekly">
          <div className="flex items-start gap-2">
            <div className="text-xs uppercase tracking-[0.16em] text-forest/70 font-bold">By source / campaign / week</div>
            <ExportButton testId="export-csv-acquisition"
              onClick={() => downloadCsv(csvFilename("acquisition", days), weekly, ["week", "source", "campaign", "count"])} />
          </div>
          {weekly.length === 0 ? (
            <div className="text-sm text-neutral-500 mt-4">No signups in this range yet.</div>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-neutral-400 uppercase tracking-wide">
                    <th className="py-1.5 pr-4 font-medium">Week</th>
                    <th className="py-1.5 pr-4 font-medium">Source</th>
                    <th className="py-1.5 pr-4 font-medium">Campaign</th>
                    <th className="py-1.5 text-right font-medium">Signups</th>
                  </tr>
                </thead>
                <tbody>
                  {weekly.slice(0, 20).map((r, i) => (
                    <tr key={i} className="border-t border-neutral-100" data-testid={`acq-row-${i}`}>
                      <td className="py-1.5 pr-4 font-mono text-neutral-600">{r.week}</td>
                      <td className="py-1.5 pr-4 text-forest">{r.source}</td>
                      <td className="py-1.5 pr-4 text-neutral-600">{r.campaign || "—"}</td>
                      <td className="py-1.5 text-right font-medium text-forest">{r.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [activity, setActivity] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => { api.adminAnalyticsSummary().then(setSummary).catch(() => setFailed(true)); }, []);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.adminAnalyticsActivity(days),
      api.adminAnalyticsGrowth(days),
      api.adminAnalyticsEngagement(days),
      api.adminAnalyticsTraffic(days),
    ]).then(([a, g, e, t]) => {
      if (!alive) return;
      setActivity(a); setGrowth(g); setEngagement(e); setTraffic(t);
    }).catch(() => alive && setFailed(true));
    return () => { alive = false; };
  }, [days]);

  if (failed) {
    return <div data-testid="analytics-error" className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">Couldn't load analytics — try refreshing.</div>;
  }
  if (!summary) return <div className="mt-8 text-sm text-neutral-500">Loading analytics…</div>;

  const rates = engagement?.application_rates;
  const hasTraffic = (traffic?.total_views || 0) > 0;

  return (
    <div data-testid="admin-analytics" className="mt-2">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard testId="stat-online-now" live label="Online now" value={summary.online_now} sub="active in last 5 min" />
        <StatCard testId="stat-dau" label="Active today" value={summary.dau_today} sub="DAU — signed-in users" />
        <StatCard testId="stat-wau" label="Weekly active" value={summary.wau} sub="WAU — last 7 days" />
        <StatCard testId="stat-mau" label="Monthly active" value={summary.mau} sub="MAU — last 30 days" />
        <StatCard testId="stat-total-users" label="Total users" value={summary.total_users} sub={`${summary.total_volunteers} volunteers · ${summary.total_orgs} orgs`} />
      </div>
      <p className="text-[11px] text-neutral-400 mt-2" data-testid="analytics-tracking-note">
        Activity {summary.activity_since ? `tracked since ${fmtDay(summary.activity_since)}` : "tracking starts with the first signed-in visit"} ·
        traffic {summary.traffic_since ? ` tracked since ${fmtDay(summary.traffic_since)}` : " tracking starts with the first consented pageview"} — no backfill, no third-party trackers.
      </p>

      {/* Range picker */}
      <div className="flex items-center justify-between mt-8 flex-wrap gap-3">
        <h3 className="font-display text-xl text-forest tracking-tight">Trends</h3>
        <RangePicker value={days} onChange={setDays} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        {/* Activity — DAU */}
        <ChartCard testId="chart-activity" title="Daily active users" icon={Activity} note="Signed-in users per day (15-min throttled touch)"
          exportTestId="export-csv-activity"
          onExport={() => downloadCsv(csvFilename("activity", days), activity?.series || [], ["day", "dau"])}>
          <ResponsiveContainer width="100%" height={220} minWidth={0}>
            <AreaChart data={activity?.series || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="day" tickFormatter={fmtDay} tick={AXIS_TICK} minTickGap={24} />
              <YAxis allowDecimals={false} tick={AXIS_TICK} width={32} />
              <Tooltip labelFormatter={fmtDay} />
              <Area type="monotone" dataKey="dau" name="DAU" stroke={FOREST} fill={FOREST} fillOpacity={0.12} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Growth — signups + cumulative */}
        <ChartCard testId="chart-growth" title="Growth — signups & cumulative" icon={TrendingUp} note="Volunteers vs organisations, with running total"
          exportTestId="export-csv-growth"
          onExport={() => downloadCsv(csvFilename("growth", days), growth?.series || [], ["day", "volunteers", "orgs", "cumulative"])}>
          <ResponsiveContainer width="100%" height={220} minWidth={0}>
            <ComposedChart data={growth?.series || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="day" tickFormatter={fmtDay} tick={AXIS_TICK} minTickGap={24} />
              <YAxis yAxisId="left" allowDecimals={false} tick={AXIS_TICK} width={32} />
              <YAxis yAxisId="right" orientation="right" allowDecimals={false} tick={AXIS_TICK} width={36} />
              <Tooltip labelFormatter={fmtDay} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="left" dataKey="volunteers" name="Volunteers" stackId="s" fill={FOREST} radius={[0, 0, 0, 0]} />
              <Bar yAxisId="left" dataKey="orgs" name="Orgs" stackId="s" fill={TERRACOTTA} radius={[3, 3, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="cumulative" name="Total users" stroke="#8a8a8a" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Engagement — applications */}
        <ChartCard testId="chart-applications" title="Applications" icon={Briefcase} note="Applications submitted per day"
          exportTestId="export-csv-applications"
          onExport={() => downloadCsv(csvFilename("applications", days), engagement?.series || [], ["day", "applications"])}>
          <ResponsiveContainer width="100%" height={220} minWidth={0}>
            <AreaChart data={engagement?.series || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="day" tickFormatter={fmtDay} tick={AXIS_TICK} minTickGap={24} />
              <YAxis allowDecimals={false} tick={AXIS_TICK} width={32} />
              <Tooltip labelFormatter={fmtDay} />
              <Area type="monotone" dataKey="applications" name="Applications" stroke={TERRACOTTA} fill={TERRACOTTA} fillOpacity={0.12} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Engagement — hours + opportunities */}
        <ChartCard testId="chart-hours-opps" title="Hours logged & roles posted" icon={Clock3} note={`${engagement?.hours_total ?? 0}h logged · ${engagement?.opportunities_posted ?? 0} roles posted in range`}
          exportTestId="export-csv-hours"
          onExport={() => downloadCsv(csvFilename("hours-roles", days), engagement?.series || [], ["day", "hours", "opportunities", "gatherings"])}>
          <ResponsiveContainer width="100%" height={220} minWidth={0}>
            <ComposedChart data={engagement?.series || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="day" tickFormatter={fmtDay} tick={AXIS_TICK} minTickGap={24} />
              <YAxis allowDecimals={false} tick={AXIS_TICK} width={32} />
              <Tooltip labelFormatter={fmtDay} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="hours" name="Hours logged" fill={FOREST} radius={[3, 3, 0, 0]} />
              <Line type="monotone" dataKey="opportunities" name="Roles posted" stroke={AMBER} strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Application rates + gatherings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div data-testid="application-rates" className="bg-white border border-neutral-200 rounded-xl p-5">
          <div className="text-xs uppercase tracking-[0.16em] text-forest/70 font-bold flex items-center gap-1.5">
            <Briefcase className="w-3.5 h-3.5" /> Application outcomes ({days}d)
          </div>
          {rates && rates.total > 0 ? (
            <div className="flex flex-wrap gap-2 mt-4">
              <RateChip label="Accepted" count={rates.accepted} pct={rates.accepted_pct} tone="green" />
              <RateChip label="Rejected" count={rates.rejected} pct={rates.rejected_pct} tone="red" />
              <RateChip label="Pending" count={rates.pending} pct={rates.pending_pct} tone="amber" />
            </div>
          ) : (
            <div className="text-sm text-neutral-500 mt-4">No applications in this range.</div>
          )}
        </div>
        <div data-testid="gatherings-stats" className="bg-white border border-neutral-200 rounded-xl p-5">
          <div className="text-xs uppercase tracking-[0.16em] text-forest/70 font-bold flex items-center gap-1.5">
            <PartyPopper className="w-3.5 h-3.5" /> Gatherings ({days}d)
          </div>
          <div className="flex gap-8 mt-4">
            <div>
              <div className="font-display text-3xl text-forest leading-none">{engagement?.gatherings_created ?? 0}</div>
              <div className="text-xs text-neutral-500 mt-1">created</div>
            </div>
            <div>
              <div className="font-display text-3xl text-forest leading-none">{engagement?.gathering_rsvps ?? 0}</div>
              <div className="text-xs text-neutral-500 mt-1">RSVPs on those</div>
            </div>
          </div>
        </div>
      </div>

      {/* Traffic */}
      <div className="flex items-center gap-2 mt-8">
        <h3 className="font-display text-xl text-forest tracking-tight flex items-center gap-2">
          <MousePointerClick className="w-5 h-5" /> Traffic
        </h3>
        <span className="text-[11px] text-neutral-400">first-party beacon · consented sessions only</span>
      </div>
      {hasTraffic ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
          <div className="lg:col-span-2">
            <ChartCard testId="chart-traffic" title="Page views & visitors" icon={Radio} note={`${traffic.total_views} views in range`}
              exportTestId="export-csv-traffic"
              onExport={() => downloadCsv(csvFilename("traffic", days), traffic.series, ["day", "views", "visitors"])}>
              <ResponsiveContainer width="100%" height={220} minWidth={0}>
                <ComposedChart data={traffic.series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="day" tickFormatter={fmtDay} tick={AXIS_TICK} minTickGap={24} />
                  <YAxis allowDecimals={false} tick={AXIS_TICK} width={32} />
                  <Tooltip labelFormatter={fmtDay} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="views" name="Views" stroke={FOREST} fill={FOREST} fillOpacity={0.1} strokeWidth={2} />
                  <Line type="monotone" dataKey="visitors" name="Visitors" stroke={TERRACOTTA} strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
          <div data-testid="top-pages" className="bg-white border border-neutral-200 rounded-xl p-5">
            <div className="flex items-start gap-2">
              <div className="text-xs uppercase tracking-[0.16em] text-forest/70 font-bold flex items-center gap-1.5">
                <CalendarDays className="w-3.5 h-3.5" /> Top pages
              </div>
              <ExportButton testId="export-csv-top-pages"
                onClick={() => downloadCsv(csvFilename("top-pages", days), traffic.top_pages, ["path", "views"])} />
            </div>
            <div className="mt-4 space-y-2">
              {traffic.top_pages.map((p) => {
                const max = traffic.top_pages[0]?.views || 1;
                return (
                  <div key={p.path} data-testid={`top-page-${p.path}`}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-mono text-forest truncate">{p.path}</span>
                      <span className="text-neutral-500 shrink-0 ml-2">{p.views}</span>
                    </div>
                    <div className="h-1.5 bg-neutral-100 rounded-full mt-1">
                      <div className="h-1.5 bg-forest/60 rounded-full" style={{ width: `${Math.max(4, (p.views / max) * 100)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div data-testid="traffic-empty" className="mt-4 bg-white border border-dashed border-neutral-300 rounded-xl p-8 text-center">
          <Radio className="w-6 h-6 text-neutral-300 mx-auto" />
          <div className="text-sm text-neutral-500 mt-2">No page views recorded yet.</div>
          <div className="text-xs text-neutral-400 mt-1">The beacon only fires for visitors who chose "Accept all" in the cookie banner — data will appear as consented visitors browse.</div>
        </div>
      )}

      {/* Acquisition (17-C item 9) */}
      <AcquisitionSection days={days} />

      {/* Quality flags */}
      <QualityFlags />
    </div>
  );
}
